import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, FormProvider, useWatch, useFormState } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSTextField, RMSSelect, RMSDatePicker } from '../../../shared/components/form-controls';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import RMSRecordHistoryButton from '../../../shared/components/modules/RMSRecordHistoryButton';
import SearchPersonAutoComplete from '../../../shared/components/modules/SearchPersonAutoComplete';
import CreateFormResolver from '../../../shared/utility/createFormResolver';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import useUserDictionaryOptions from '../../hooks/useUserDictionaryOptions';
import { savePlaceEmployment } from '../../services/userService';
import AddPersonDialog from './AddPersonDialog';
import { DICT_SELECT_PROPS, DEFAULT_VALUES } from '../../constants/placeEmployeeFormConstants';
import placeEmployeeSchema from '../../schema/placeEmployeeSchema';

const PlaceEmployeeDialog = ({ open, onClose, ptsPlaceID, employee = null, onSaved }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData.user);
  const [submitting, setSubmitting] = useState(false);
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [addSupervisorDialogOpen, setAddSupervisorDialogOpen] = useState(false);
  const isEditMode = Boolean(employee?.ptsPersonID);

  const methods = useForm({
    resolver: CreateFormResolver(placeEmployeeSchema),
    mode: 'onSubmit',
    defaultValues: { ...DEFAULT_VALUES, ...(employee || {}) },
  });

  const { isDirty } = useFormState({ control: methods.control });
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const {
    positionOptions,
    occupationOptions,
    rankOptions,
    employmentStatusOptions,
    payRateOptions,
    terminationReasonOptions,
  } = useUserDictionaryOptions();

  const ptsPersonID = useWatch({ control: methods.control, name: 'ptsPersonID' });
  const ptsSupervisorID = useWatch({ control: methods.control, name: 'ptsSupervisorID' });
  const hired = useWatch({ control: methods.control, name: 'Hired' });
  const terminated = useWatch({ control: methods.control, name: 'Terminated' });

  const handleEmployeeCreated = useCallback(
    (person) => {
      methods.setValue('ptsPersonID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddEmployeeDialogOpen(false);
    },
    [methods]
  );

  const handleSupervisorCreated = useCallback(
    (person) => {
      methods.setValue('ptsSupervisorID', person?.ptsPersonID ?? null, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setAddSupervisorDialogOpen(false);
    },
    [methods]
  );

  const onSubmit = useCallback(
    async (data) => {
      const payload = {
        ptsPersonID: data.ptsPersonID,
        ptsPlaceID,
        DepartmentName: data.DepartmentName || null,
        Position: data.Position || null,
        Occupation: data.Occupation || null,
        Rank: data.Rank || null,
        Status: data.Status || null,
        Pay: data.Pay || null,
        Rate: data.Rate || null,
        ptsSupervisorID: data.ptsSupervisorID || null,
        Shift: data.Shift || null,
        Hired: data.Hired || null,
        Terminated: data.Terminated || null,
        TerminationReason: data.TerminationReason || null,
        UpdatedBy: currentUser?.Username,
      };

      if (!isEditMode) payload.CreatedBy = currentUser?.Username;

      await executeApiAction({
        setLoading: setSubmitting,
        dispatch,
        action: () => savePlaceEmployment(payload),
        onSuccess: (savedEmployee) => {
          dispatch(
            notify(
              isEditMode ? 'Employee updated successfully' : 'Employee added successfully',
              'success'
            )
          );
          onSaved?.(savedEmployee);
        },
      });
    },
    [isEditMode, ptsPlaceID, currentUser, dispatch, onSaved]
  );

  const handleSave = methods.handleSubmit(onSubmit);

  const requestClose = useCallback(() => {
    if (isDirty) {
      requestConfirm(
        'You have unsaved changes on this employee. Are you sure you want to close without saving?',
        () => onClose?.(),
        'Unsaved Changes'
      );
      return;
    }

    onClose?.();
  }, [isDirty, requestConfirm, onClose]);

  return (
    <>
      <RMSCommonDialog
        open={open}
        title={isEditMode ? 'Edit Employee' : 'Add Employee'}
        onClose={requestClose}
        onSave={handleSave}
        loading={submitting}
        saveDisabled={submitting}
        maxWidth="sm"
        fullWidth
        closeOnBackdropClick={false}
        className="place-employee-dialog"
        saveButton={{
          show: true,
          type: 'simple',
          label: 'Save',
          variant: 'contained',
          color: 'primary',
        }}
        cancelButton={{
          show: true,
          type: 'simple',
          label: 'Close',
          variant: 'outlined',
          color: 'secondary',
        }}
        leftActions={
          isEditMode && (
            <RMSRecordHistoryButton
              disabled={!employee?.Created}
              created={employee?.Created}
              createdBy={employee?.CreatedBy}
              updated={employee?.Updated}
              updatedBy={employee?.UpdatedBy}
            />
          )
        }>
        <FormProvider {...methods}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SearchPersonAutoComplete
                name="ptsPersonID"
                label="Employee"
                defaultPersonId={ptsPersonID}
                disabled={isEditMode}
                showAddIcon={!isEditMode}
                onAddIconClick={() => setAddEmployeeDialogOpen(true)}
                showDetailsIcon={false}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSTextField
                name="DepartmentName"
                label="Department Name"
                inputProps={{ maxLength: 40 }}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="Position"
                label="Position"
                options={positionOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="Occupation"
                label="Occupation"
                options={occupationOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect name="Rank" label="Rank" options={rankOptions} {...DICT_SELECT_PROPS} />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect
                name="Status"
                label="Status"
                options={employmentStatusOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSTextField name="Pay" label="Pay" type="number" />
            </Grid>
            <Grid item xs={6}>
              <RMSSelect name="Rate" label="Rate" options={payRateOptions} {...DICT_SELECT_PROPS} />
            </Grid>
            <Grid item xs={6}>
              <SearchPersonAutoComplete
                name="ptsSupervisorID"
                label="Supervisor"
                defaultPersonId={ptsSupervisorID}
                onAddIconClick={() => setAddSupervisorDialogOpen(true)}
                showDetailsIcon={false}
              />
            </Grid>
            <Grid item xs={6}>
              <RMSTextField name="Shift" label="Shift" inputProps={{ maxLength: 50 }} />
            </Grid>
            <Grid item xs={6}>
              <RMSDatePicker name="Hired" label="Hired" maxDate={terminated || undefined} />
            </Grid>
            <Grid item xs={6}>
              <RMSDatePicker name="Terminated" label="Terminated" minDate={hired || undefined} />
            </Grid>
            <Grid item xs={12}>
              <RMSSelect
                name="TerminationReason"
                label="Termination Reason"
                options={terminationReasonOptions}
                {...DICT_SELECT_PROPS}
              />
            </Grid>
          </Grid>
        </FormProvider>

        {addEmployeeDialogOpen && (
          <AddPersonDialog
            open={addEmployeeDialogOpen}
            onClose={() => setAddEmployeeDialogOpen(false)}
            onPersonCreated={handleEmployeeCreated}
          />
        )}

        {addSupervisorDialogOpen && (
          <AddPersonDialog
            open={addSupervisorDialogOpen}
            onClose={() => setAddSupervisorDialogOpen(false)}
            onPersonCreated={handleSupervisorCreated}
          />
        )}
      </RMSCommonDialog>

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </>
  );
};

export default PlaceEmployeeDialog;
